import Close from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import type { OptionsObject } from 'notistack';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Fragment, useCallback, useRef } from 'react';

import type { ConvoV2NotificationProps } from '../modules/customNotifications/ConvoV2Notification';
import { selectElectronCaptionMode } from '../selectors/ui';
import { addNotification } from '../store/slices/ccMode';
import { useAppDispatch, useAppSelector } from '../store/store';

export type NotificationOptions = OptionsObject & {
  convoV2Icon?: React.ReactNode;
  isDesktop?: boolean;
  subtitle?: string;
  onClick?: () => void;
  maxNotification?: number;
};
export interface ButtonOptions {
  url?: string;
  action?: () => void;
  text: string;
}

export const useNotifications = () => {
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const electronCaptionMode = useAppSelector(selectElectronCaptionMode);
  const dispatch = useAppDispatch();
  const theme = useTheme();

  // Track active notifications by maxNotification limit
  const notificationQueues = useRef<Map<number, string[]>>(new Map());

  // Helper function to manage notification queues
  const manageNotificationQueue = useCallback(
    (maxNotification: number, newNotificationId: string) => {
      const queue = notificationQueues.current.get(maxNotification) || [];

      // Close oldest notifications if at limit
      while (queue.length >= maxNotification) {
        const oldestId = queue.shift();
        if (oldestId) {
          closeSnackbar(oldestId);
        }
      }

      // Add new notification to queue
      queue.push(newNotificationId);
      notificationQueues.current.set(maxNotification, queue);
    },
    [closeSnackbar]
  );

  // Helper function to remove notification from queue when closed
  const removeFromQueue = useCallback((notificationId: string) => {
    for (const [limit, queue] of notificationQueues.current.entries()) {
      const index = queue.indexOf(notificationId);
      if (index !== -1) {
        queue.splice(index, 1);
        if (queue.length === 0) {
          notificationQueues.current.delete(limit);
        }
        break;
      }
    }
  }, []);

  const notify = useCallback(
    (text: string, options?: NotificationOptions, button?: ButtonOptions) => {
      if (options?.variant?.includes('soloDia')) {
        return enqueueSnackbar(text, {
          ...options,
          closeSnackbar,
          // Disable preventDuplicate when maxNotification is used
          preventDuplicate: options.maxNotification ? false : options.preventDuplicate,
        });
      }
      if (options?.variant === 'convoV2') {
        const variantOptions: OptionsObject & ConvoV2NotificationProps = {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          icon: options.convoV2Icon,
          ...options,
          closeSnackbar: (key) => {
            if (key) {
              removeFromQueue(key as string);
            }
            closeSnackbar(key);
          },
          isDesktop: options.isDesktop,
          subtitle: options.subtitle,
          onClick: options.onClick,
          // Disable preventDuplicate when maxNotification is used
          preventDuplicate: options.maxNotification ? false : options.preventDuplicate,
        };
        const notificationId = enqueueSnackbar(text, variantOptions);

        // Manage queue if maxNotification is specified
        if (options.maxNotification && notificationId) {
          manageNotificationQueue(options.maxNotification, notificationId as string);
        }

        return notificationId;
      }
      if (electronCaptionMode) {
        dispatch(
          addNotification({
            text,
            duration: options?.autoHideDuration || undefined,
            timestamp: Date.now(),
            //@ts-expect-error
            type: options?.variant || 'default',
            link: button?.url,
            linkText: button?.text,
            linkAction: button?.action,
          })
        );
      } else {
        const message = options?.subtitle ? (
          <div>
            <div>{text}</div>
            <div
              style={{
                fontSize: '0.875em',
                marginTop: '4px',
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              }}
            >
              {options.subtitle}
            </div>
          </div>
        ) : (
          text
        );

        const notificationId = enqueueSnackbar(message, {
          ...(options || {}),
          // Disable preventDuplicate when maxNotification is used
          preventDuplicate: options?.maxNotification ? false : options?.preventDuplicate,
          action: (key) => (
            <Fragment>
              {button && (
                <Button
                  href={button.url || ''}
                  onClick={button.action}
                  target="_blank"
                  rel="noreferrer noopener"
                  variant="outlined"
                  style={{
                    borderColor:
                      options?.color || (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'white'),
                    borderRadius: 20,
                    color: options?.color || (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'white'),
                    fontSize: '0.9em',
                    textTransform: 'none',
                  }}
                >
                  {button.text}
                </Button>
              )}
              <Button
                onClick={() => {
                  if (key) {
                    removeFromQueue(key as string);
                  }
                  closeSnackbar(key);
                }}
                style={{
                  color: 'inherit',
                }}
              >
                <Close />
              </Button>
            </Fragment>
          ),
        });

        // Manage queue if maxNotification is specified
        if (options?.maxNotification && notificationId) {
          manageNotificationQueue(options.maxNotification, notificationId as string);
        }

        return notificationId;
      }
    },
    [
      closeSnackbar,
      enqueueSnackbar,
      electronCaptionMode,
      theme.palette.mode,
      dispatch,
      manageNotificationQueue,
      removeFromQueue,
    ]
  );

  return {
    notify,
  };
};
